import { useMemo } from "react";

import { Icon_People, Icon_People_Slash } from "@shapr3d/frontend-lib";
import { Icon_Document_Filled24 } from "@shapr3d/frontend-lib";
import { FormattedMessage } from "react-intl";

import useTranslation from "../../../../../hooks/useTranslation";
import type { SharingSpace } from "../../../../../types";

import style from "./SpaceRow.module.css";

type SpaceRowProps =
  | {
      variant: "owner-private";
      space: Extract<SharingSpace, { spaceType: "private" }>;
      projectName?: string;
    }
  | {
      variant: "owner-team";
      space: Extract<SharingSpace, { spaceType: "team" }>;
      projectName?: string;
    }
  | {
      variant: "no-access";
      projectName: string;
    };

export const SpaceRow = (props: SpaceRowProps) => {
  const { m } = useTranslation();

  const { icon, labelProps, descriptionProps } = useMemo(() => {
    switch (props.variant) {
      case "owner-private": {
        return {
          icon: <Icon_People_Slash />,
          labelProps: {
            id: m.ShareProjectModal.SpaceAccessLabelPrivate.id,
            defaultMessage: m.ShareProjectModal.SpaceAccessLabelPrivate.defaultMessage,
          },
          descriptionProps: null,
        };
      }

      case "owner-team": {
        const { space } = props;
        const userCount = space.spaceUsers?.length ?? 0;
        const spaceName = space.spaceName;

        return {
          icon: <Icon_People />,
          labelProps: spaceName
            ? {
                id: m.ShareProjectModal.SpaceAccessLabelWithName.id,
                defaultMessage: m.ShareProjectModal.SpaceAccessLabelWithName.defaultMessage,
                values: { spaceName },
              }
            : {
                id: m.ShareProjectModal.SpaceAccessLabelGeneric.id,
                defaultMessage: m.ShareProjectModal.SpaceAccessLabelGeneric.defaultMessage,
              },
          descriptionProps: {
            id: m.ShareProjectModal.SpaceAccessDescription.id,
            defaultMessage: m.ShareProjectModal.SpaceAccessDescription.defaultMessage,
            values: { count: userCount },
          },
        };
      }

      case "no-access": {
        const { projectName } = props;
        return {
          icon: <Icon_Document_Filled24 />,
          labelProps: {
            id: m.ShareProjectModal.SpaceAccessLabelNoAccess.id,
            defaultMessage: m.ShareProjectModal.SpaceAccessLabelNoAccess.defaultMessage,
            values: { projectName },
          },
          descriptionProps: {
            id: m.ShareProjectModal.SpaceAccessDescriptionNoAccess.id,
            defaultMessage: m.ShareProjectModal.SpaceAccessDescriptionNoAccess.defaultMessage,
            values: { projectName },
          },
        };
      }
    }
  }, [props, m.ShareProjectModal]);

  return (
    <div className={style.accessSection} role="listitem">
      <div className={style.iconWrapper} aria-hidden="true">
        {icon}
      </div>
      <div className={style.spaceAccessWrapper}>
        <div className={style.spaceAccessLabel}>
          <FormattedMessage {...labelProps} />
        </div>
        {descriptionProps && (
          <div className={style.spaceAccessDescription}>
            <FormattedMessage {...descriptionProps} />
          </div>
        )}
      </div>
      <div className={style.canAccessWrapper}>
        <div>Can Access</div>
      </div>
    </div>
  );
};
